import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, RefreshCw, HeartPulse, Siren, Smile, Sunrise } from "lucide-react";
import { Page, BrandHeader } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HistoryInsights } from "@/components/HistoryInsights";
import { ExportPanel } from "@/components/ExportPanel";
import { SessionPrepCard } from "@/components/SessionPrep";
import { PsychNotesCard } from "@/components/PsychNotes";
import { fourFConfig, moodColor, moodLevel } from "@/lib/config";
import { entryKind, historySpeech } from "@/lib/entrySpeech";
import { ListenButton } from "@/components/AiNote";
import { isUnfinished } from "@/lib/sections";
import { useFlips, useSessions } from "@/lib/store";
import { sudsVar } from "@/lib/theme";

const FILTERS = [["all", "All"], ["checkins", "Check-ins"], ["triggers", "Triggers"], ["moods", "Moods"], ["flips", "Flips"]];

function SessionItem({ s, onClick, i }) {
  const cfg = s.survivalResponse ? fourFConfig(s.survivalResponse) : null;
  const Icon = s.path === "emergency" ? Siren : s.path === "morning" ? Sunrise : s.moodOnly ? Smile : HeartPulse;
  return (
    <button onClick={onClick} data-testid={`history-item-${i}`} className="tf-card w-full p-4 flex items-center gap-4 text-left hover:border-t-brand transition-colors">
      <span className="w-12 h-12 rounded-2xl grid place-items-center text-white shrink-0" style={{ background: s.preSuds == null ? moodColor(s.moodNow) : sudsVar(s.postSuds ?? s.preSuds) }}><Icon size={20} /></span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-t-on truncate">
          {entryKind(s)}{s.preSuds != null ? ` · ${s.preSuds}` : ""}{s.postSuds != null ? ` → ${s.postSuds}` : ""}
        </span>
        <span className="block text-[13px] text-t-muted truncate">
          {new Date(s.timestamp).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
          {s.moodNow != null ? ` · ${moodLevel(s.moodNow)?.label}` : ""}{s.copingMode ? ` · ${s.copingMode}` : ""}
        </span>
        {s.triggerNote ? <span className="block text-[13px] text-t-on2 italic truncate">“{s.triggerNote}”</span> : null}
      </span>
      {isUnfinished(s) ? <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-t-surface3 text-t-on2 shrink-0" data-testid={`history-unfinished-${i}`}>Unfinished</span> : null}
      {cfg ? <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ color: `var(--${cfg.key})`, background: `color-mix(in srgb, var(--${cfg.key}) 16%, transparent)` }}>{cfg.label}</span> : null}
    </button>
  );
}

function FlipItem({ f, onClick, i }) {
  return (
    <button onClick={onClick} data-testid={`history-flip-${i}`} className="tf-card w-full p-4 flex items-center gap-4 text-left hover:border-t-brand transition-colors">
      <span className="w-12 h-12 rounded-2xl grid place-items-center bg-t-brand2 text-t-brand shrink-0"><RefreshCw size={20} /></span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-t-on truncate">Flip the Script · {f.flips.map((x) => x.schema).join(", ")}</span>
        <span className="block text-[13px] text-t-muted">{new Date(f.timestamp).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
      </span>
    </button>
  );
}

export default function History() {
  const navigate = useNavigate();
  const { data: sessions = [] } = useSessions();
  const { data: flips = [] } = useFlips();
  const [filter, setFilter] = useState("all");
  const [exportOpen, setExportOpen] = useState(false);

  const items = useMemo(() => {
    const ss = sessions.filter((s) =>
      filter === "all" || (filter === "checkins" && !s.moodOnly) || (filter === "triggers" && s.triggered) || (filter === "moods" && s.moodNow != null));
    const fs = filter === "all" || filter === "flips" ? flips.filter((f) => !f.sessionId || filter === "flips") : [];
    return [...(filter === "flips" ? [] : ss.map((s) => ({ k: "s", t: s.timestamp, s }))), ...fs.map((f) => ({ k: "f", t: f.timestamp, f }))].sort((a, b) => b.t.localeCompare(a.t));
  }, [sessions, flips, filter]);

  return (
    <Page>
      <div className="md:hidden"><BrandHeader /></div>
      <div className="mt-6 md:mt-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-t-on">History</h1>
          <p className="mt-2 text-t-on2">{sessions.length} check-in{sessions.length === 1 ? "" : "s"} · {flips.length} flip{flips.length === 1 ? "" : "s"}</p>
        </div>
        <Btn size="md" icon={<FileDown size={17} />} label="Export PDF report" onClick={() => setExportOpen(true)} testId="open-export-button" />
      </div>
      <PsychNotesCard />
      <SessionPrepCard />
      <Tabs defaultValue="entries" className="mt-6">
        <TabsList className="rounded-full bg-t-surface3 p-1 h-auto">
          <TabsTrigger value="entries" className="rounded-full px-5 h-9 data-[state=active]:bg-t-surface data-[state=active]:text-t-on text-t-muted" data-testid="history-tab-entries">Entries</TabsTrigger>
          <TabsTrigger value="insights" className="rounded-full px-5 h-9 data-[state=active]:bg-t-surface data-[state=active]:text-t-on text-t-muted" data-testid="history-tab-insights">Insights</TabsTrigger>
        </TabsList>
        <TabsContent value="entries" className="mt-5">
          <div className="flex gap-2 flex-wrap mb-5">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`history-filter-${k}`}
                className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${filter === k ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface2 text-t-on2 border-t-border"}`}>{l}</button>
            ))}
          </div>
          {items.length ? <div className="mb-4 flex items-center gap-3"><ListenButton text={historySpeech(items)} testId="history-listen" /><span className="text-sm text-t-muted">Hear your last {Math.min(5, items.length)} {items.length === 1 ? "entry" : "entries"}</span></div> : null}
          <div className="flex flex-col gap-3">
            {items.length === 0 ? (
              <div className="tf-card p-10 text-center" data-testid="history-empty">
                <p className="font-semibold text-t-on">No check-ins yet</p>
                <p className="text-sm text-t-muted mt-1">Your entries will gather here as you check in.</p>
                <Btn className="mt-5 mx-auto" size="md" label="Check in now" onClick={() => navigate("/")} testId="history-checkin" />
              </div>
            ) : items.map((it, i) => it.k === "s"
              ? <SessionItem key={it.s.id} s={it.s} i={i} onClick={() => navigate(`/entry/${it.s.id}`)} />
              : <FlipItem key={it.f.id} f={it.f} i={i} onClick={() => navigate(`/entry/${it.f.id}`)} />)}
          </div>
        </TabsContent>
        <TabsContent value="insights" className="mt-5">
          <HistoryInsights sessions={sessions} flips={flips} />
        </TabsContent>
      </Tabs>
      <ExportPanel open={exportOpen} onOpenChange={setExportOpen} sessions={sessions} flips={flips} />
    </Page>
  );
}
